import { motion } from 'framer-motion';
import {
  Users,
  Skull,
  HelpCircle,
  Music,
  Film
} from 'lucide-react';
import type { GameMode } from '@shared/types';

interface GameModeBadgeProps {
  mode: GameMode;
  showDescription?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

const MODE_INFO: Record<GameMode, { 
  name: string; 
  icon: typeof Music; 
  color: string; 
  bgColor: string;
  description: string;
}> = {
  classic: {
    name: 'Classique',
    icon: Music,
    color: 'text-blue-400',
    bgColor: 'bg-blue-500/20',
    description: 'Trouvez le titre et l\'artiste',
  },
  teams: {
    name: 'Équipes',
    icon: Users,
    color: 'text-green-400',
    bgColor: 'bg-green-500/20',
    description: 'Jouez en équipe',
  },
  'battle-royale': {
    name: 'Battle Royale',
    icon: Skull,
    color: 'text-red-400',
    bgColor: 'bg-red-500/20',
    description: 'Dernier survivant !',
  },
  quiz: {
    name: 'Quiz',
    icon: HelpCircle,
    color: 'text-cyan-400',
    bgColor: 'bg-cyan-500/20',
    description: 'Questions à choix multiples',
  },
  deaf: {
    name: 'Deaf Test',
    icon: Film,
    color: 'text-pink-400',
    bgColor: 'bg-pink-500/20',
    description: 'Clip vidéo muet',
  },
};

export default function GameModeBadge({ mode, showDescription = false, size = 'md' }: GameModeBadgeProps) {
  const info = MODE_INFO[mode] || MODE_INFO.classic;
  const Icon = info.icon;
  
  const sizeClasses = {
    sm: 'px-2 py-1 text-xs',
    md: 'px-3 py-1.5 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  
  const iconSizes = {
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
    lg: 'w-5 h-5',
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      className={`inline-flex items-center gap-2 rounded-full ${info.bgColor} ${sizeClasses[size]}`}
    >
      <Icon className={`${iconSizes[size]} ${info.color}`} />
      <span className={`font-semibold ${info.color}`}>{info.name}</span>
      {showDescription && (
        <span className="text-dark-400 hidden sm:inline">
          - {info.description}
        </span>
      )}
    </motion.div>
  );
}

// Export des infos pour utilisation externe
export { MODE_INFO };
